import { nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * The other half of the Overview's "Make something" tiles.
 *
 * A tile is only worth having if it lands you where the next thing you do is
 * type. Navigating and selecting the tab is the easy half; this is the half
 * that has to happen on the destination page — open the create affordance, and
 * put the cursor in the first field.
 *
 * One convention, six readers: `?create=<key>`.
 *
 * @param {string} key       the tile this page answers to
 * @param {Function} open    opens the create affordance and focuses its first field
 */
export function useCreateOnArrival(key, open) {
  const route = useRoute();
  const router = useRouter();

  // Whether this page was reached from a tile. Captured before the param is
  // stripped, because the cancel path below still needs to know.
  const arrivedFromTile = ref(false);

  onMounted(async () => {
    if (route.query.create !== key) return;

    arrivedFromTile.value = true;

    // Strip the param so a refresh — or a back-then-forward — does not reopen
    // a form the person has already dealt with.
    //
    // Awaited and tolerant of rejection: the hub guard runs on this replace
    // too, and returns false when there are unsaved changes elsewhere. Fired
    // and forgotten, that left `create` in the URL and the form reopening on
    // the next refresh — the exact thing this is here to prevent.
    const query = { ...route.query };
    delete query.create;

    const url = router.resolve({ path: route.path, query }).href;

    try {
      const result = await router.replace({ path: route.path, query });

      // vue-router resolves (doesn't throw) when a guard cancels navigation.
      if (result === false || result) {
        window.history.replaceState(window.history.state, '', url);
      }
    } catch {
      window.history.replaceState(window.history.state, '', url);
    }

    // After the tab has had a tick to render: on a tabbed page the panel the
    // affordance lives in may not exist yet at mount.
    await nextTick();
    open();
  });

  /**
   * Cancelling from a tile returns you to where you came from.
   *
   * Only when arrived from a tile: cancelling a banner opened from the Content
   * page itself must leave you on Content, not fling you to a page you were
   * never on.
   */
  const cancelReturn = () => {
    if (arrivedFromTile.value) {
      router.push('/overview');
    }
  };

  return { arrivedFromTile, cancelReturn };
}
